import { useState } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Logo } from "./Logo";
import type { Role } from "../lib/types";

const ROLE_LABEL: Record<Role, string> = {
  SUPER_ADMIN: "Administración",
  OPERATIONS: "Operaciones",
  INSPECTOR: "Inspector",
  FINANCE: "Finanzas",
  CUSTOMER: "Cliente",
};

function navItems(role: Role): { to: string; label: string }[] {
  if (role === "CUSTOMER") {
    return [
      { to: "/", label: "Panel" },
      { to: "/cases", label: "Mis expedientes" },
    ];
  }
  const items = [
    { to: "/", label: "Panel" },
    { to: "/cases", label: "Expedientes" },
    { to: "/pipeline", label: "Pipeline" },
    { to: "/clients", label: "Clientes" },
    { to: "/invitations", label: "Invitaciones" },
  ];
  return items;
}

const navLinkClass = ({ isActive }: { isActive: boolean }) =>
  `rounded-lg px-3 py-2 text-sm font-medium transition ${isActive ? "bg-white/10 text-white" : "text-white/60 hover:bg-white/5 hover:text-white"}`;

export function Layout() {
  const { principal, logout } = useAuth();
  // Mobile only: the sidebar is hidden below `sm`, so the header opens the same navigation.
  const [menuOpen, setMenuOpen] = useState(false);
  if (!principal) return null;
  const items = navItems(principal.role);
  const isCustomer = principal.role === "CUSTOMER";

  return (
    <div className="flex min-h-dvh">
      <aside className="hidden w-60 shrink-0 flex-col border-r border-line bg-navy px-4 py-5 text-white sm:flex">
        <div className="px-2">
          <Logo className="w-40 h-auto" />
          <div className="mt-1.5 text-xs text-white/50">{isCustomer ? "Portal de cliente" : "CRM interno"}</div>
        </div>
        <nav className="mt-8 flex flex-col gap-1">
          {items.map((it) => (
            <NavLink key={it.to} to={it.to} end={it.to === "/"} className={navLinkClass}>
              {it.label}
            </NavLink>
          ))}
        </nav>
        <div className="mt-auto border-t border-white/10 pt-4">
          <div className="px-2 text-xs text-white/50">{ROLE_LABEL[principal.role]}</div>
          <button onClick={() => void logout()} className="mt-2 w-full rounded-lg px-3 py-2 text-left text-sm text-white/70 transition hover:bg-white/5 hover:text-white">
            Cerrar sesión
          </button>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-10 bg-navy text-white sm:hidden">
          <div className="flex items-center justify-between px-5 py-3">
            {/* The logo asset is near-square with transparent padding: crop to the wordmark. */}
            <Logo className="h-10 w-28 object-cover" />
            <button
              type="button"
              onClick={() => setMenuOpen((open) => !open)}
              aria-expanded={menuOpen}
              aria-controls="mobile-nav"
              aria-label={menuOpen ? "Cerrar menú" : "Abrir menú"}
              className="-mr-2 rounded-lg p-2 text-white/80 transition hover:bg-white/10 hover:text-white"
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                {menuOpen ? <path d="M6 6l12 12M18 6L6 18" /> : <path d="M4 7h16M4 12h16M4 17h16" />}
              </svg>
            </button>
          </div>
          {menuOpen && (
            <div id="mobile-nav" className="border-t border-white/10 px-4 pb-4 pt-2">
              <nav className="flex flex-col gap-1">
                {items.map((it) => (
                  <NavLink key={it.to} to={it.to} end={it.to === "/"} onClick={() => setMenuOpen(false)} className={navLinkClass}>
                    {it.label}
                  </NavLink>
                ))}
              </nav>
              <div className="mt-3 flex items-center justify-between border-t border-white/10 px-3 pt-3">
                <span className="text-xs text-white/50">{ROLE_LABEL[principal.role]}</span>
                <button onClick={() => void logout()} className="text-sm text-white/70 transition hover:text-white">
                  Cerrar sesión
                </button>
              </div>
            </div>
          )}
        </header>
        <main className="mx-auto w-full max-w-6xl flex-1 px-5 py-8 sm:px-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
